import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Logo } from '@/components/Logo';
import { BrowserFrame, PhoneFrame, Waves } from '@/components/landing/Frames';
import { APP_SCREENS } from '@/config/mobileApps';

export function useCreateCta() {
  const { session } = useAuth();
  const navigate = useNavigate();
  return () => navigate(session ? '/create' : '/login?next=/create');
}

const LINKS = [['#comment', 'Comment ça marche'], ['#styles', 'Styles'], ['#decouverte', 'Découverte'], ['#tarifs', 'Tarifs'], ['#mobile', 'Application'], ['#faq', 'FAQ']];

export function LandingNav() {
  const { session } = useAuth();
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-black/60 backdrop-blur-xl" data-testid="landing-nav">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3 sm:px-8">
        <Logo variant="full" className="h-10 sm:h-11" />
        <nav className="hidden items-center gap-7 text-sm text-white/70 lg:flex">
          {LINKS.map(([h, l]) => <a key={h} href={h} className="transition-colors hover:text-brand">{l}</a>)}
        </nav>
        <div className="flex items-center gap-2">
          {session ? (
            <Link to="/app" data-testid="landing-open-studio" className="rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-black btn-glow">Ouvrir mon studio</Link>
          ) : (
            <>
              <Link to="/login" data-testid="landing-login" className="rounded-full px-3 py-2.5 text-sm font-medium text-white hover:text-brand sm:px-4">Connexion</Link>
              <Link to="/register" data-testid="landing-register" className="rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-black btn-glow">S'inscrire</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export function Hero() {
  const create = useCreateCta();
  const { session } = useAuth();
  return (
    <section className="relative overflow-hidden pb-24 pt-32 sm:pt-40" data-testid="landing-hero">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_70%_20%,rgba(255,165,0,0.16),transparent_70%)]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 opacity-25"><Waves bars={90} /></div>
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.05fr_1fr]">
        <div className="landing-reveal">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand/30 bg-brand/10 px-4 py-1.5 font-mono text-xs uppercase tracking-widest text-brand">
            <Sparkles className="h-3.5 w-3.5" /> Le studio de ton imagination
          </span>
          <h1 className="mt-7 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl">
            Ton imagination mérite sa propre <span className="text-gradient-orange">musique.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base text-white/70 sm:text-lg">
            Transforme tes idées, tes émotions et tes histoires en chansons originales grâce à l'intelligence artificielle.
          </p>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <button onClick={create} data-testid="hero-create-button"
                    className="group flex items-center justify-center gap-2 rounded-full bg-brand px-8 py-4 font-semibold text-black btn-glow transition-transform hover:scale-[1.03]">
              Créer ma musique <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <a href="#comment" data-testid="hero-discover-button"
               className="flex items-center justify-center rounded-full border border-white/15 px-8 py-4 font-semibold text-white transition-colors hover:border-brand/60 hover:text-brand">
              Découvrir MyMusic IA
            </a>
          </div>
          {session && <Link to="/app" className="mt-5 inline-block text-sm text-white/60 hover:text-brand" data-testid="hero-open-studio">Ouvrir mon studio →</Link>}
        </div>
        <div className="landing-reveal relative pb-10 [animation-delay:150ms] sm:pl-10">
          <BrowserFrame src={APP_SCREENS.desktop} alt="Studio de création MyMusic IA" />
          <div className="landing-float absolute -bottom-6 left-0 hidden w-28 sm:block lg:-left-6 lg:w-32">
            <PhoneFrame src={APP_SCREENS.androidPhone} alt="Accueil MyMusic IA sur mobile" />
          </div>
        </div>
      </div>
    </section>
  );
}
